import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of, timer } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

// Indicadores del admin rediseñado: Inicio, Pendientes, fichas y búsqueda global.
// Endpoints de solo lectura en `dashboard/*` (ver beer-proyect-api/src/dashboard/insights.service.ts).

export interface MoneySummary {
  totalCollected: number; subtotal: number; discounts: number; netRevenue: number; itbms: number;
  barCostNet: number; barCostTax: number; gatewayCost: number; commissionCost: number; profit: number; profitPct: number;
  breakageCost: number; pendingStopsCost: number;
  stopsSold: number; stopsUsed: number; redemptionPct: number;
  stopsSoldExpired: number; stopsUsedExpired: number; redemptionExpiredPct: number;
  itemsSold: number; itemsWithVisit: number; itemsCompleted: number; giftItems: number;
}

export interface SummaryResponse {
  range: { from: string; to: string };
  money: MoneySummary;
  reservations: { paid: number; gifts: number; created: number; cancelled: number; unpaid: number };
  avgTicket: number;
  customers: { buyers: number; returning: number; returningPct: number; returningRevenue: number };
  funnel: { checkoutItems: number; paidItems: number; withVisit: number; completed: number };
  byDay: { date: string; netRevenue: number; profit: number; items: number }[];
  visitsByWeekday: number[];
  visitsByHour: number[];
  origin: { key: string; label: string; netRevenue: number }[];
  avgHoursToFirstVisit: number | null;
}

export interface Liabilities {
  total: number;
  bars: { total: number; settledUnpaid: number; settledUnpaidCount: number; unsettledLastMonth: number; unsettledMonth: string };
  affiliates: { total: number; pending: number; pendingCount: number; available: number; availableCount: number; requests: number; requestsCount: number };
}

@Injectable({ providedIn: 'root' })
export class InsightsService {
  private base = environment.apiUrl + 'dashboard/';

  /** Contador de Pendientes para el menú (se refresca al entrar y cada 5 minutos). */
  readonly pendingCount$ = new BehaviorSubject<number | null>(null);
  private badgeStarted = false;

  constructor(private http: HttpClient) {}

  summary(from: string, to: string): Observable<SummaryResponse> {
    return this.http.post<SummaryResponse>(this.base + 'summary', { startDate: from, endDate: to });
  }

  pending(): Observable<any> {
    return this.http.get<any>(this.base + 'pending').pipe(
      map((res) => { this.pendingCount$.next(res?.total ?? 0); return res; })
    );
  }

  liabilities(): Observable<Liabilities> {
    return this.http.get<Liabilities>(this.base + 'liabilities');
  }

  breweries(from: string, to: string): Observable<any[]> {
    return this.http.get<any[]>(this.base + 'breweries', { params: new HttpParams().set('from', from).set('to', to) });
  }

  brewery(id: number, from: string, to: string): Observable<any> {
    return this.http.get<any>(this.base + 'brewery/' + id, { params: new HttpParams().set('from', from).set('to', to) });
  }

  eventsPerformance(onlyActive = false): Observable<any[]> {
    return this.http.get<any[]>(this.base + 'events-performance', { params: new HttpParams().set('active', onlyActive ? '1' : '0') });
  }

  event(id: number): Observable<any> {
    return this.http.get<any>(this.base + 'event/' + id);
  }

  discountCodes(from?: string, to?: string): Observable<any[]> {
    let params = new HttpParams();
    if (from) params = params.set('from', from);
    if (to) params = params.set('to', to);
    return this.http.get<any[]>(this.base + 'discount-codes', { params });
  }

  customers(): Observable<any[]> {
    return this.http.get<any[]>(this.base + 'customers');
  }

  customer(id: number): Observable<any> {
    return this.http.get<any>(this.base + 'customer/' + id);
  }

  search(q: string): Observable<{ results: any[] }> {
    return this.http.get<{ results: any[] }>(this.base + 'search', { params: new HttpParams().set('q', q) });
  }

  startPendingBadge(): void {
    if (this.badgeStarted) return;
    this.badgeStarted = true;
    timer(0, 5 * 60 * 1000).pipe(
      switchMap(() => this.pending().pipe(catchError(() => of(null))))
    ).subscribe();
  }

  refreshPendingBadge(): void {
    this.pending().pipe(catchError(() => of(null))).subscribe();
  }
}
